import {
  GoogleAuthProvider,
  OAuthProvider,
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  getAuth,
  getRedirectResult,
  linkWithPopup,
  linkWithRedirect,
  onAuthStateChanged,
  signInAnonymously,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut as fbSignOut,
  type Auth,
  type AuthProvider,
  type User,
  type UserCredential,
} from "firebase/auth";
import { getFirebaseApp, isFirebaseConfigured } from "./firebase";
import { isInAppBrowser, systemBrowserName } from "./inAppBrowser";

/**
 * Lazily resolves the Firebase Auth instance. Returns `null` when Firebase
 * isn't configured for this environment so callers can degrade gracefully
 * (the sign-in UI shows a "not configured" message instead of crashing).
 *
 * The auth emulator is connected automatically when
 * `VITE_FIREBASE_USE_EMULATORS=true`.
 */

let cachedAuth: Auth | null = null;
let emulatorConnected = false;
let redirectResultDrained = false;

export function getFirebaseAuth(): Auth | null {
  if (cachedAuth) return cachedAuth;
  const app = getFirebaseApp();
  if (!app) return null;
  cachedAuth = getAuth(app);

  if (!emulatorConnected) {
    const useEmulators =
      (import.meta.env.VITE_FIREBASE_USE_EMULATORS as string | undefined) ===
      "true";
    if (useEmulators) {
      try {
        connectAuthEmulator(cachedAuth, "http://localhost:9099", {
          disableWarnings: true,
        });
      } catch {
        // Already connected — safe to ignore.
      }
      emulatorConnected = true;
    }
  }

  // Drain any pending redirect result once per page load. Without this,
  // a successful `signInWithRedirect` round-trip is still resolved by
  // `onAuthStateChanged`, but any *error* from the redirect (e.g. the
  // user denied access at Google) is silently swallowed. Awaiting it
  // also pre-warms the internal auth iframe used by `signInWithPopup`,
  // which materially improves popup reliability on iOS Safari (the
  // popup opens within the same user-gesture tick instead of after a
  // ~hundreds-of-ms iframe fetch).
  if (!redirectResultDrained) {
    redirectResultDrained = true;
    void getRedirectResult(cachedAuth)
      .then((result) => {
        if (result) recordSignInResult(result);
      })
      .catch(() => {
      // The error (if any) will already have been surfaced by the
      // sign-in attempt that triggered the redirect; here we just
      // need to drain the promise so the SDK can settle.
    });
  }

  return cachedAuth;
}

/**
 * Detects browsers where `signInWithRedirect` to the default Firebase
 * `*.firebaseapp.com` auth domain is known to fail with "missing
 * initial state" because the browser partitions third-party storage
 * (Safari ITP, iOS WKWebView, in-app browsers, etc.). See
 * https://firebase.google.com/docs/auth/web/redirect-best-practices.
 *
 * Exported for tests; consumers should prefer the high-level
 * `signInWithGoogle` flow which already handles this.
 */
export function isStoragePartitionedBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";

  // Real iOS devices (any browser there is WebKit under the hood and
  // shares the same storage-partitioning behavior).
  if (/iPad|iPhone|iPod/.test(ua)) return true;

  // iPadOS 13+ masquerades as desktop Safari but still reports touch
  // points + Mac platform. Catch that here.
  const nav = navigator as Navigator & { maxTouchPoints?: number };
  if (
    /Macintosh/.test(ua) &&
    typeof nav.maxTouchPoints === "number" &&
    nav.maxTouchPoints > 1
  ) {
    return true;
  }

  // Desktop Safari (not Chrome/Edge/Firefox-on-iOS, which all include
  // their own tokens).
  const isSafari =
    /Safari\//.test(ua) &&
    !/Chrome\/|Chromium\/|CriOS\/|FxiOS\/|EdgiOS\/|Edg\//.test(ua);
  if (isSafari) return true;

  return false;
}

let lastSignInResult: { uid: string; isNewUser: boolean } | null = null;
/** uid of the anonymous session the last provider sign-in was linked onto. */
let lastLinkedUid: string | null = null;

/** True iff `uid` just went from anonymous to an account via linking. */
export function peekSignInWasLink(uid: string): boolean {
  return lastLinkedUid === uid;
}

function credentialFromLinkError(provider: AuthProvider, err: unknown) {
  if (provider instanceof GoogleAuthProvider) return GoogleAuthProvider.credentialFromError(err as never);
  if (provider instanceof OAuthProvider) return OAuthProvider.credentialFromError(err as never);
  return null;
}

function recordSignInResult(result: Parameters<typeof getAdditionalUserInfo>[0]): void {
  try {
    const info = getAdditionalUserInfo(result);
    if (info) lastSignInResult = { uid: result.user.uid, isNewUser: info.isNewUser };
  } catch {
    // analytics-only signal; the metadata fallback still applies
  }
}

/**
 * Firebase's own "this sign-in created the account" flag for `uid`, or
 * `null` when the sign-in result hasn't been seen (e.g. it resolved
 * after the auth listener ran). Read by the telemetry bridge.
 */
export function peekSignInIsNewUser(uid: string): boolean | null {
  return lastSignInResult?.uid === uid ? lastSignInResult.isNewUser : null;
}

export function isAuthAvailable(): boolean {
  return isFirebaseConfigured();
}

export interface AuthUser {
  uid: string;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
  /**
   * A Firebase anonymous session (the feng shui first run's "try before
   * you sign up"). Has a real uid, so callables and Firestore work; still
   * counts as signed-out for anything that means "has an account".
   */
  isAnonymous: boolean;
}

export function toAuthUser(u: User | null): AuthUser | null {
  if (!u) return null;
  return {
    uid: u.uid,
    displayName: u.displayName,
    email: u.email,
    photoURL: u.photoURL,
    isAnonymous: u.isAnonymous === true,
  };
}

/**
 * Subscribe to auth state changes. Resolves immediately with `null` when
 * Firebase isn't configured (so subscribers don't hang waiting for a
 * `READY` state that will never come).
 */
export function subscribeAuth(
  cb: (user: AuthUser | null) => void
): () => void {
  const auth = getFirebaseAuth();
  if (!auth) {
    cb(null);
    return () => {};
  }
  return onAuthStateChanged(auth, (u) => cb(toAuthUser(u)));
}

/**
 * Resolve once Firebase has settled the initial auth state — i.e. the
 * persisted user (if any) has been restored from IndexedDB and the first
 * `onAuthStateChanged` callback has fired. Resolves to `null` when
 * Firebase isn't configured.
 *
 * Why this exists: `auth.currentUser` is `null` between page load and
 * the moment Firebase finishes restoring the persisted session, which
 * can take hundreds of milliseconds. Anything that runs in the very
 * first render tick — most painfully the post-Stripe-Checkout
 * activation effect — sees that null and treats it as "user is signed
 * out". That's the cause of the upsell modal still appearing after a
 * successful upgrade: the polling helper bails on a phantom signed-out
 * state before it ever forces the token refresh that would surface the
 * just-stamped `stripeRole` claim.
 *
 * Uses `auth.authStateReady()` (Firebase 9+). The defensive
 * `typeof === "function"` check is there so unit tests with a stubbed
 * Auth object don't have to grow yet another mock surface.
 */
export async function waitForAuthReady(): Promise<AuthUser | null> {
  const auth = getFirebaseAuth();
  if (!auth) return null;
  if (typeof auth.authStateReady === "function") {
    try {
      await auth.authStateReady();
    } catch {
      // authStateReady never rejects in practice; ignore defensively.
    }
  }
  return toAuthUser(auth.currentUser);
}

export interface SignInOptions {
  /**
   * When `true`, falls back to `signInWithRedirect` if popup fails (e.g.
   * popup blocked, in-app webview). Defaults to `true`.
   */
  redirectOnPopupFailure?: boolean;
}

export async function signInWithGoogle(
  opts: SignInOptions = {}
): Promise<AuthUser> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return signInWithProvider(provider, opts);
}

/**
 * Sign in with Apple. Needs the Apple provider enabled in the Firebase
 * console (Services ID + key from the Apple developer account); until then
 * Firebase answers `auth/operation-not-allowed`, surfaced as `unknown`.
 */
export async function signInWithApple(
  opts: SignInOptions = {}
): Promise<AuthUser> {
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  return signInWithProvider(provider, opts);
}

/**
 * The anonymous first-run session: a real uid with no account, so the
 * metered callables and per-user Firestore work before anyone signs up.
 * Returns the current user when one already exists (anonymous or not).
 */
export async function ensureAnonymousSession(): Promise<AuthUser> {
  const auth = getFirebaseAuth();
  if (!auth) {
    throw new AuthError("not-configured", "Sign-in isn't configured for this environment.");
  }
  if (typeof auth.authStateReady === "function") {
    try {
      await auth.authStateReady();
    } catch {
      /* never rejects in practice */
    }
  }
  if (auth.currentUser) return toAuthUser(auth.currentUser)!;
  try {
    const result = await signInAnonymously(auth);
    return toAuthUser(result.user)!;
  } catch (err) {
    const code = (err as { code?: string } | null)?.code ?? "";
    if (code === "auth/network-request-failed") {
      throw new AuthError("network", "Couldn't reach the server. Check your connection and try again.");
    }
    if (code === "auth/admin-restricted-operation" || code === "auth/operation-not-allowed") {
      throw new AuthError("not-configured", "Anonymous sessions aren't enabled for this project.");
    }
    throw new AuthError("unknown", (err as Error).message || "Couldn't start a session.");
  }
}

/**
 * Popup (then redirect) sign-in with an OAuth provider. When the current
 * session is anonymous, the provider is *linked* to it so the uid — and
 * everything stored under it — survives. If that Google/Apple identity
 * already has its own account, Firebase refuses the link
 * (`credential-already-in-use`); we then sign into the existing account
 * instead, which is what the visitor meant.
 */
async function signInWithProvider(
  provider: GoogleAuthProvider | OAuthProvider,
  opts: SignInOptions = {}
): Promise<AuthUser> {
  const { redirectOnPopupFailure = true } = opts;
  const auth = getFirebaseAuth();
  if (!auth) {
    throw new AuthError(
      "not-configured",
      "Sign-in isn't configured for this environment."
    );
  }
  // Short-circuit inside Instagram / Facebook / TikTok / … in-app webviews.
  // Google (and Apple) refuse OAuth in embedded webviews
  // (`disallowed_useragent`), so BOTH the popup and the redirect fail there —
  // the redirect doubly so because partitioned storage also breaks the
  // "initial state" round-trip. `AuthProviders` already swaps the buttons
  // for "Open in Safari/Chrome" in that case; this guard covers every other
  // caller (Start together, the guide) so nobody fires a doomed attempt.
  if (isInAppBrowser()) {
    throw new AuthError(
      "in-app-browser",
      `Sign-in doesn't work inside in-app browsers. Open this page in ${systemBrowserName()} to continue.`
    );
  }

  const anon = auth.currentUser?.isAnonymous ? auth.currentUser : null;

  try {
    let result: UserCredential;
    if (anon) {
      try {
        result = await linkWithPopup(anon, provider);
        lastLinkedUid = anon.uid;
      } catch (err) {
        const code = (err as { code?: string } | null)?.code ?? "";
        if (code !== "auth/credential-already-in-use") throw err;
        const cred = credentialFromLinkError(provider, err);
        if (!cred) throw err;
        result = await signInWithCredential(auth, cred);
      }
    } else {
      result = await signInWithPopup(auth, provider);
    }
    recordSignInResult(result);
    const user = toAuthUser(result.user);
    if (!user) {
      throw new AuthError("unknown", "Sign-in did not return a user.");
    }
    return user;
  } catch (err) {
    const code = (err as { code?: string } | null)?.code ?? "";

    if (code === "auth/popup-closed-by-user") {
      throw new AuthError("cancelled", "Sign-in was cancelled.");
    }
    if (code === "auth/cancelled-popup-request") {
      // A second popup was requested before the first resolved. Treat
      // as a no-op cancel rather than a hard error.
      throw new AuthError("cancelled", "Sign-in was cancelled.");
    }
    if (code === "auth/network-request-failed") {
      throw new AuthError(
        "network",
        "Couldn't reach Google. Check your connection and try again."
      );
    }

    const popupBlocked =
      code === "auth/popup-blocked" ||
      code === "auth/operation-not-supported-in-this-environment";

    if (popupBlocked) {
      // On iOS / Safari / in-app webviews the `signInWithRedirect`
      // round-trip against the default `*.firebaseapp.com` auth
      // domain fails with "Unable to process request due to missing
      // initial state…" because third-party storage is partitioned.
      // Surface a clear, actionable error instead of redirecting into
      // a known broken flow.
      if (isStoragePartitionedBrowser()) {
        throw new AuthError(
          "popup-blocked",
          "Couldn't open the Google sign-in popup. Please allow pop-ups for this site in your browser settings and try again, or open this link in Chrome."
        );
      }

      if (redirectOnPopupFailure) {
        if (anon) await linkWithRedirect(anon, provider);
        else await signInWithRedirect(auth, provider);
        // Redirect navigates the page away — control doesn't resume here.
        throw new AuthError("redirecting", "Redirecting to sign-in…");
      }

      throw new AuthError(
        "popup-blocked",
        "Couldn't open the Google sign-in popup. Please allow pop-ups for this site and try again."
      );
    }

    throw new AuthError(
      "unknown",
      (err as Error).message || "Sign-in failed. Please try again."
    );
  }
}

export async function signOut(): Promise<void> {
  const auth = getFirebaseAuth();
  if (!auth) return;
  await fbSignOut(auth);
}

/**
 * Finish an email one-time-code sign-in with the custom token the
 * `verifyEmailCode` callable minted. When the token is for the current
 * anonymous uid (the server set the email on it), the session keeps its
 * uid and everything under it.
 */
export async function signInWithServerToken(
  token: string,
  linked: boolean
): Promise<AuthUser> {
  const auth = getFirebaseAuth();
  if (!auth) {
    throw new AuthError("not-configured", "Sign-in isn't configured for this environment.");
  }
  const result = await signInWithCustomToken(auth, token);
  if (linked) lastLinkedUid = result.user.uid;
  // A custom-token session has no `additionalUserInfo`; the bridge reads
  // `linked` / account metadata instead.
  const user = toAuthUser(result.user);
  if (!user) throw new AuthError("unknown", "Sign-in did not return a user.");
  return user;
}

/**
 * Dev-only sign-in path for the multiplayer testing harness. Uses
 * email/password against the Firebase Auth emulator so two browser
 * profiles can authenticate as distinct users without needing real
 * Google accounts.
 *
 * No-ops (throws) outside `VITE_FIREBASE_USE_EMULATORS=true` so this
 * can never be called against production. Vite tree-shakes the body
 * away in production builds because the env var resolves to `"false"`.
 *
 * If the user doesn't exist yet (e.g. first run after wiping
 * `emulator-data/`), this auto-creates them. Note: callers that depend
 * on a verified email (e.g. `acceptShareInvite`) must also run the
 * seed script — `createUserWithEmailAndPassword` doesn't mark
 * `emailVerified: true`.
 */
export async function signInWithTestUser(
  email: string,
  password = "password"
): Promise<AuthUser> {
  const useEmulators =
    (import.meta.env.VITE_FIREBASE_USE_EMULATORS as string | undefined) ===
    "true";
  if (!useEmulators) {
    throw new AuthError(
      "not-configured",
      "Test sign-in is only available when VITE_FIREBASE_USE_EMULATORS=true."
    );
  }
  const auth = getFirebaseAuth();
  if (!auth) {
    throw new AuthError(
      "not-configured",
      "Sign-in isn't configured for this environment."
    );
  }
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    recordSignInResult(result);
    const user = toAuthUser(result.user);
    if (!user) {
      throw new AuthError("unknown", "Sign-in did not return a user.");
    }
    return user;
  } catch (err) {
    const code = (err as { code?: string } | null)?.code ?? "";
    if (code === "auth/user-not-found" || code === "auth/invalid-credential") {
      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      recordSignInResult(result);
      const user = toAuthUser(result.user);
      if (!user) {
        throw new AuthError("unknown", "Sign-up did not return a user.");
      }
      return user;
    }
    throw new AuthError(
      "unknown",
      (err as Error).message || "Test sign-in failed."
    );
  }
}

export type AuthErrorKind =
  | "not-configured"
  | "cancelled"
  | "network"
  | "popup-blocked"
  | "in-app-browser"
  | "redirecting"
  | "rate-limited"
  | "unknown";

export class AuthError extends Error {
  readonly kind: AuthErrorKind;
  constructor(kind: AuthErrorKind, message: string) {
    super(message);
    this.kind = kind;
    this.name = "AuthError";
  }
}
